import { MISSION_IDS, ORDER_INVALID_REASONS, ORDER_WARNINGS } from '@badger-sentinel/rules';
import { z } from 'zod';
import { zBand, zCell, zTeamId } from './enums.js';
import { zContactAlias } from './view.js';
/**
 * Orders on the wire (§8.4).
 *
 * The one thing to notice: a `CONTACT` target carries a **contact alias**, not an
 * asset id (decision M2-D2). The client has never been told a real enemy id and
 * cannot send one. The server translates the alias back at exactly one place —
 * the order handler — and an alias it does not recognise is a `BAD_REQUEST`,
 * never a silently dropped target.
 *
 * `SLOT` and `BAND` targets are unaliased because they name places, not objects.
 *
 * `OWN_ASSET` is the one arm that names an object by its **true id**, and it is
 * safe for exactly the reason the others are not: it is one of the sender's own
 * assets, which they were told about by their own order of battle. See its arm
 * below.
 */
const strict = (shape) => z.object(shape).strict();
export const zOrderTarget = z.discriminatedUnion('kind', [
    strict({ kind: z.literal('NONE') }),
    strict({ kind: z.literal('SELF') }),
    strict({ kind: z.literal('BAND'), band: zBand }),
    strict({ kind: z.literal('REGION'), region: z.string().min(1).max(64) }),
    strict({ kind: z.literal('CONTACT'), contact: zContactAlias }),
    /**
     * One of the sender's **own** assets, by its true id (M5 Q24).
     *
     * Eight missions act on friendly kit — the whole defensive cyber half (RB
     * §5.6.5), `ELECTRONIC_PROTECTION`, `DEPLOY` and `TTC_REASSIGN` — and none of
     * them could be expressed before this arm existed. `zContactAlias` covers
     * enemy assets and this team's own phantoms only, so naming one's own station
     * failed the schema, and overloading `CONTACT` to carry a raw id would have
     * destroyed the guarantee that makes it worth having: **a `CONTACT` target
     * still means "something you were never told the identity of."** That is why
     * `zContactAlias` stays strict here and a second arm carries the plain id.
     *
     * It is a wire arm only. The domain's `OrderTarget` already accepts an
     * `AssetId` inside `CONTACT` (RB §5.2.3 — the engine must not be able to tell
     * a phantom from a real subject by shape), so the server translates this into
     * exactly that and no resolver, mission table or validator changes.
     *
     * **The server checks ownership**, the same check `phase1-cyber.ts` and
     * `phase2-em.ts` already make at resolution, and it refuses an id this team
     * does not own with the *same* message it gives for an id that does not exist
     * — otherwise this arm would be a probe for enemy asset ids.
     */
    strict({ kind: z.literal('OWN_ASSET'), asset: z.string().min(1).max(64) }),
    strict({ kind: z.literal('SLOT'), band: zBand, slot: z.number().int().min(1).max(24) }),
    /**
     * An inbound missile warning track (RB §5.7.5). Unaliased, because an MW event id
     * is generated at resolution and names a *track*, not an object — it tells the
     * holder nothing they were not already told by the release.
     */
    strict({ kind: z.literal('MW_EVENT'), event: z.string().min(1).max(64) }),
]);
/**
 * Mission parameters, typed rather than free-form.
 *
 * `zOrderDraft.params` stays a loose scalar record on the wire — widening it to a
 * union per mission would make the schema a maintenance burden for no safety, since
 * the engine validates every parameter anyway. What this table buys is a single
 * place naming the keys each mission reads, so the client's order form and
 * `engine-runner` agree.
 */
export const MISSION_PARAM_KEYS = {
    ESCALATE_PRIVILEGE: ['to'],
    DEPLOY_PAYLOAD: ['payload'],
    INTERCEPT: ['missiles'],
    TRANSFER: ['destSlot'],
    RELOCATE: ['region'],
};
/**
 * One order as the client writes it.
 *
 * `team` and `cell` are deliberately **absent**: they come from the seat token, so
 * a client cannot claim to be another cell by writing a different value here. The
 * server fills them in from the authenticated seat (§3.3 invariant 2).
 */
export const zOrderDraft = strict({
    asset: z.string().min(1).max(64),
    mission: z.enum(MISSION_IDS),
    target: zOrderTarget,
    params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});
/**
 * The whole cell's draft, sent as a set rather than as a delta.
 *
 * Idempotent by construction: the client sends what it wants the cell's orders to
 * be, and the server stores exactly that. A delta protocol would need
 * reconciliation after a reconnect, which is a reconnect bug waiting for minute 4
 * of a 5-minute window (§8.5).
 */
export const zOrdersDraftPayload = strict({
    turn: z.number().int().positive(),
    orders: z.array(zOrderDraft).max(64),
});
export const zOrdersSubmitPayload = strict({
    turn: z.number().int().positive(),
    /** False un-submits: a cell may change its mind until the window closes (§8.4). */
    submitted: z.boolean(),
});
/** Why an order was refused at submission. Rendered next to the offending row. */
export const zOrderRejection = strict({
    index: z.number().int().nonnegative(),
    reason: z.enum(ORDER_INVALID_REASONS),
    detail: z.string(),
}).readonly();
/**
 * The state of one cell's orders, echoed back after every change.
 *
 * `accepted` is what the server has stored. It is not necessarily what the client
 * sent: an order that fails submission-time validation is reported in `rejected`
 * and kept out of the stored set, so nobody discovers at resolution that half
 * their turn was never recorded.
 */
/**
 * A note on an order that was *accepted* (decision M3-D13).
 *
 * ROE is priced, not forbidden (RB §7.3): a violating order is legal, costs 3 VP
 * and doubles its escalation. The warning is how the crew finds out before they
 * commit rather than in the after-action review.
 */
export const zOrderWarning = strict({
    index: z.number().int().nonnegative(),
    warning: z.enum(ORDER_WARNINGS),
    detail: z.string(),
}).readonly();
export const zCellOrdersState = strict({
    turn: z.number().int(),
    cell: zCell,
    accepted: z.array(zOrderDraft).readonly(),
    rejected: z.array(zOrderRejection).readonly(),
    warnings: z.array(zOrderWarning).readonly(),
    submitted: z.boolean(),
    updatedAtMs: z.number().int(),
}).readonly();
/** Readiness across the team — who has submitted, who has not (§8.4 `orders.state`). */
export const zTeamOrdersState = strict({
    turn: z.number().int(),
    team: zTeamId,
    cells: z
        .array(strict({
        cell: zCell,
        claimed: z.boolean(),
        connected: z.boolean(),
        submitted: z.boolean(),
        orderCount: z.number().int(),
    }).readonly())
        .readonly(),
    /**
     * The team's drafts in full — present only for a seat whose capabilities allow
     * it, which today means CREWCOM with `crewcomSeesDrafts` on (§6.2).
     */
    drafts: z
        .array(strict({ cell: zCell, orders: z.array(zOrderDraft).readonly() }).readonly())
        .readonly()
        .nullable(),
}).readonly();
/** SDA/ISR publishing a contact to the rest of the team (RB §2.4, §5.8.4). */
export const zPublishContactPayload = strict({
    contact: zContactAlias,
});
//# sourceMappingURL=orders.js.map